import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  WifiIcon,
  Bars3Icon,
  EnvelopeIcon,
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  MapIcon
} from '@heroicons/react/24/outline';
import { useAuthStatus } from '../../hooks/useAuth'; // ✅ Import auth hook
import { useTranslation } from 'react-i18next';
import { T } from '../T';

const HamburgerDrawer = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuthStatus(); // ✅ Get admin status
  const { t } = useTranslation();

  // Close drawer on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close drawer on escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // ✅ FIXED: Handle navigation with token sharing for admin panels
  const handleNavigation = useCallback((path, action) => {
    // Close the drawer first
    setIsOpen(false);

    if (action === 'discovery') {
      // If on home page, trigger the discovery banner click event
      if (location.pathname === '/' || location.pathname === '/discovery') {
        const event = new CustomEvent('openDiscovery');
        window.dispatchEvent(event);
      } else {
        navigate('/');
        setTimeout(() => {
          const event = new CustomEvent('openDiscovery');
          window.dispatchEvent(event);
        }, 100);
      }
    } else if (action === 'all-events') {
      // If on home page, trigger the all events banner click event
      if (location.pathname === '/' || location.pathname === '/events') {
        const event = new CustomEvent('openAllEvents');
        window.dispatchEvent(event);
      } else {
        navigate('/');
        setTimeout(() => {
          const event = new CustomEvent('openAllEvents');
          window.dispatchEvent(event);
        }, 100);
      }
    } else if (action === 'external') {
      // ✅ FIXED: Copy token and user data for admin panel authentication
      const venueToken = localStorage.getItem('venue_app_token');
      const userRole = localStorage.getItem('user_role');
      const userRegion = localStorage.getItem('user_region');
      const userEmail = localStorage.getItem('user_email');
      const userPhone = localStorage.getItem('user_phone');
      const userName = localStorage.getItem('user_name');
      const loginMethod = localStorage.getItem('user_login_method');
      
      if (venueToken) {
        // ✅ Store token with admin key for admin panel
        localStorage.setItem('adminToken', venueToken);
        localStorage.setItem('selectedRegion', userRegion || 'ae');
        
        // ✅ Store user data for admin panel
        const adminUserData = {
          role: userRole,
          email: userEmail,
          phone: userPhone,
          name: userName,
          loginMethod: loginMethod || 'unknown',
          region: userRegion || 'ae'
        };
        localStorage.setItem('adminUser', JSON.stringify(adminUserData));
        
        console.log('✅ Authentication data shared with admin panel:', {
          hasToken: !!venueToken,
          role: userRole,
          region: userRegion,
          targetUrl: path
        });
      } else {
        console.warn('⚠️ No venue token found for admin panel authentication');
      }
      
      // Open external link in new tab
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      // For other pages, navigate normally
      navigate(path);
    }
  }, [navigate, location.pathname]);

  // ✅ Define all menu items (including admin-only items) — i18n keys from drawer.*
  const allMenuItems = [
    {
      id: 'discovery',
      labelKey: 'drawer.discovery',
      descKey: 'drawer.discoveryDesc',
      icon: <MagnifyingGlassIcon className="w-6 h-6" />,
      path: '/discovery',
      action: 'discovery',
      adminOnly: false
    },
    {
      id: 'speed-test',
      labelKey: 'drawer.speedTest',
      descKey: 'drawer.speedTestDesc',
      icon: <WifiIcon className="w-6 h-6" />,
      path: '/speed-test',
      action: 'navigate',
      adminOnly: false
    },
    {
      id: 'all-events',
      labelKey: 'drawer.allEvents',
      descKey: 'drawer.allEventsDesc',
      icon: <CalendarIcon className="w-6 h-6" />,
      path: '/events',
      action: 'navigate',
      adminOnly: false
    },
    {
      id: 'contact',
      labelKey: 'drawer.contactUs',
      descKey: 'drawer.contactUsDesc',
      icon: <EnvelopeIcon className="w-6 h-6" />,
      path: '/contact',
      action: 'navigate',
      adminOnly: false
    },
    {
      id: 'venue-dashboard',
      labelKey: 'drawer.venueDashboard',
      descKey: 'drawer.venueDashboardDesc',
      icon: <ChartBarIcon className="w-6 h-6" />,
      path: '/venue-dashboard',
      action: 'navigate',
      adminOnly: false
    },
    {
      id: 'venue-dashboard-legacy',
      labelKey: 'drawer.venueDashboardLegacy',
      descKey: 'drawer.venueDashboardLegacyDesc',
      icon: <ChartBarIcon className="w-6 h-6" />,
      path: '/venue-dashboard-legacy',
      action: 'navigate',
      adminOnly: true
    },
    {
      id: 'admin-panel',
      labelKey: 'drawer.adminPanel',
      descKey: 'drawer.adminPanelDesc',
      icon: <Cog6ToothIcon className="w-6 h-6" />,
      path: 'https://admin.honestlee.app/',
      action: 'external',
      adminOnly: true
    },
    {
      id: 'legacy-map',
      labelKey: 'drawer.legacyMap',
      descKey: 'drawer.legacyMapDesc',
      icon: <MapIcon className="w-6 h-6" />,
      path: '/legacy-map',
      action: 'navigate',
      adminOnly: false
    }
  ];

  // ✅ Filter menu items based on admin status
  const menuItems = allMenuItems.filter(item => {
    if (item.adminOnly) {
      return isAdmin; // Show only if user is admin
    }
    return true; // Show all non-admin items
  });

  const styles = {
    hamburgerButton: {
      padding: '8px',
      borderRadius: '50%',
      backgroundColor: '#184a99',
      border: 'none',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 10000,
      backdropFilter: 'blur(4px)',
    },
    drawer: {
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      width: 'min(320px, 85vw)',
      maxWidth: '320px',
      background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
      zIndex: 10001,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '4px 0 20px rgba(0, 0, 0, 0.15)',
    },
    drawerHeader: {
      padding: '24px 20px 16px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    drawerTitle: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    drawerContent: {
      flex: 1,
      padding: '20px',
      overflowY: 'auto',
    },
    menuList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    menuItem: {
      width: '100%',
    },
    menuItemButton: {
      width: '100%',
      padding: '16px',
      borderRadius: '12px',
      border: 'none',
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'all 0.2s ease',
      textAlign: 'left',
    },
    menuItemIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
    },
    menuItemContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      flex: 1,
    },
    menuItemLabel: {
      fontSize: '16px',
      fontWeight: '600',
      color: 'white',
      lineHeight: 1.2,
    },
    menuItemDescription: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.8)',
      lineHeight: 1.3,
    },
    closeButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      transition: 'all 0.2s ease',
    },
    drawerFooter: {
      padding: '20px',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
    appInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    appLogo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      fontSize: '18px',
      fontWeight: 'bold',
    },
    appDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    },
    appName: {
      fontSize: '14px',
      fontWeight: '600',
      color: 'white',
    },
    appVersion: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.7)',
    },
  };

  return (
    <>
      {/* Hamburger Button */}
      <motion.button
        onClick={handleToggle}
        style={styles.hamburgerButton}
        aria-label={t('drawer.openMenuAria')}
        aria-expanded={isOpen}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#184a99';
        }}
      >
        <Bars3Icon style={{ height: 24, width: 24, color: 'white' }} />
      </motion.button>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={styles.backdrop}
            onClick={handleClose}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ 
              type: 'spring', 
              damping: 25, 
              stiffness: 200 
            }}
            style={styles.drawer}
            role="dialog"
            aria-modal="true"
            aria-label={t('drawer.navMenuAria')}
          >
            {/* Drawer Header */}
            <div style={styles.drawerHeader}>
              <div style={styles.drawerTitle}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: 'white',
                  margin: 0,
                  lineHeight: 1.2
                }}>
                  <T k="nav.menu" />
                </h2>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  margin: 0
                }}>
                  {isAdmin ? <T k="nav.adminMenu" /> : <T k="nav.chooseOption" />}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                style={styles.closeButton}
                aria-label={t('drawer.closeMenuAria')}
              >
                <XMarkIcon className="w-6 h-6" />
              </motion.button>
            </div>

            {/* Menu Items */}
            <nav style={styles.drawerContent} role="navigation">
              <ul style={styles.menuList}>
                {menuItems.map((item) => (
                  <motion.li
                    key={item.id}
                    whileHover={{ x: 8 }}
                    whileTap={{ scale: 0.98 }}
                    style={styles.menuItem}
                  >
                    <button
                      onClick={() => handleNavigation(item.path, item.action)}
                      style={styles.menuItemButton}
                      aria-describedby={`${item.id}-description`}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={styles.menuItemIcon}>
                        {item.icon}
                      </div>
                      <div style={styles.menuItemContent}>
                        <span style={styles.menuItemLabel}><T k={item.labelKey} /></span>
                        <span
                          id={`${item.id}-description`}
                          style={styles.menuItemDescription}
                        >
                          <T k={item.descKey} />
                        </span>
                      </div>
                    </button>
                  </motion.li>
                ))}
              </ul>
            </nav>

            {/* Drawer Footer */}
            <div style={styles.drawerFooter}>
              <div style={styles.appInfo}>
                <div style={styles.appLogo}>
                  <div>H</div>
                </div>
                <div style={styles.appDetails}>
                  <span style={styles.appName}>HonestLee</span>
                  <span style={styles.appVersion}>v1.0.0</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

HamburgerDrawer.displayName = 'HamburgerDrawer';

export default HamburgerDrawer;
